import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  HostListener,
  TemplateRef
} from '@angular/core';
import { UserPreferencesService } from '@shared/services/usuario/user-preferences.service';
import { ModalStackService } from '@shared/services/modal/modal-stack.service';

@Component({
  selector: 'app-modal',
  imports: [
    CommonModule,
  ],
  templateUrl: './modal.component.html',
  styles: `
    /* Animaciones del modal */
    .modal-overlay-animated {
      animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .backdrop-animated {
      animation: backdropFadeIn 0.3s ease-out;
    }

    @keyframes backdropFadeIn {
      from {
        opacity: 0;
        backdrop-filter: blur(0px);
      }
      to {
        opacity: 1;
        backdrop-filter: blur(4px);
      }
    }

    .modal-content-animated {
      animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    /* Transición suave para el contenido */
    .modal-content-animated > div {
      animation: contentFadeIn 0.4s ease-out 0.1s backwards;
    }

    @keyframes contentFadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  `
})
export class ModalComponent implements OnChanges, OnInit, OnDestroy {

  private _isOpen = false;
  private _modalId: string;
  private _hasRegisteredOpen = false; // Evita registros duplicados
  
  @Input() 
  set isOpen(value: boolean) {
    const previousValue = this._isOpen;
    this._isOpen = value;
    
    // Emitir cambio para two-way binding
    if (value !== previousValue) {
      this.isOpenChange.emit(value);
    }
    
    // Solo actuar si hay un cambio real de estado
    if (value !== previousValue) {
      if (value) {
        // Solo registrar si no se ha registrado antes
        if (!this._hasRegisteredOpen) {
          this.modalStack.onModalOpen(this._modalId);
          this._hasRegisteredOpen = true;
        }
      } else {
        // Solo desregistrar si estaba registrado
        if (this._hasRegisteredOpen) {
          this.modalStack.onModalClose(this._modalId);
          this._hasRegisteredOpen = false;
        }
      }
    }
  }
  
  get isOpen(): boolean {
    return this._isOpen;
  }
  
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<void>();
  @Output() afterOpen = new EventEmitter<void>();
  @Input() className = '';
  @Input() scrollClass = '';
  @Input() showCloseButton = true;
  @Input() isFullscreen = false;
  @Input() isNested = false; // Para modales dentro de otros modales (z-index superior)
  @Input() maxWidth: string = '600px';
  // optional template input (fallback to programmatic/template-based content)
  @Input() template?: TemplateRef<any> | null = null;

  constructor(
    private el: ElementRef,
    private userPreferencesService: UserPreferencesService,
    private modalStack: ModalStackService
  ) {
    // Generar ID único para este modal
    this._modalId = `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  ngOnInit() {
    // Si inicia abierto, registrar
    if (this.isOpen && !this._hasRegisteredOpen) {
      this.modalStack.onModalOpen(this._modalId);
      this._hasRegisteredOpen = true;
      setTimeout(() => this.afterOpen.emit(), 0);
    }
  }

  get isDarkMode(): boolean {
    const prefs = this.userPreferencesService.preferences();
    return prefs?.theme === 'dark';
  }

  ngOnDestroy() {
    // Garantizar limpieza incluso si el modal se destruye estando abierto
    // Esto previene fugas cuando se cambia de ruta o se destruye el componente padre
    if (this._hasRegisteredOpen) {
      this.modalStack.onModalClose(this._modalId);
      this._hasRegisteredOpen = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (this.isOpen) {
        // espera para renderiza el contenido
        setTimeout(() => {
          this.afterOpen.emit();
        }, 0);
      }
    }
  }

  onBackdropClick(event: MouseEvent) {
    if (!this.isFullscreen) {
      this.close.emit();
    }
  }

  onContentClick(event: MouseEvent) {
    event.stopPropagation();
  }

  /**
   * ESC cierra **solo el modal de encima**.
   *
   * <p>Cada instancia escucha en `document`, así que con dos modales apilados una sola pulsación los
   * cerraba los dos: el usuario cancelaba el diálogo anidado y perdía también la pantalla desde la
   * que lo había abierto. `ModalStackService` ya lleva la pila; aquí solo se le pregunta quién está
   * arriba.</p>
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Escape' && this.isOpen && this.modalStack.isTopModal(this._modalId)) {
      this.close.emit();
    }
  }

}
